/**
 * Section-dirty registry — the seam between the workspace and the mounted
 * sections whose unsaved edits contribute to artifact staleness
 * (summary-staleness.ts). Mirrors useSavableSections, but the payload is one
 * reactive boolean per section: dirty = "this section holds edits the server
 * has not confirmed back yet".
 *
 * Contract:
 * - a section publishes its dirty flag under a stable key while mounted;
 * - the flag must span the whole save round-trip: it drops only after the
 *   post-save refetch has landed (each autosave's `save` awaits `onRefresh`,
 *   and useAutosave advances its baseline after `save` resolves), so the
 *   projection compare sees the saved change before the dirty OR releases;
 * - unmounting removes the flag — the unmount flush saves-and-refetches, and
 *   an unmounted section holds no further edits.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface DirtySectionsRegistry {
  /** Current flags, keyed by section. Absent = clean. */
  dirty: Record<string, boolean>;
  /** Upsert a section's flag (no-op when unchanged). */
  publish: (key: string, dirty: boolean) => void;
  /** Drop a section's flag (on unmount). */
  remove: (key: string) => void;
}

export function useDirtySections(): DirtySectionsRegistry {
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  const publish = useCallback((key: string, value: boolean) => {
    setDirty((prev) =>
      prev[key] === value ? prev : { ...prev, [key]: value },
    );
  }, []);

  const remove = useCallback((key: string) => {
    setDirty((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return useMemo(() => ({ dirty, publish, remove }), [dirty, publish, remove]);
}

/** Section-side adapter: publish `dirty` while mounted, remove on unmount. */
export function usePublishSectionDirty(
  registry: DirtySectionsRegistry | undefined,
  key: string,
  dirty: boolean,
): void {
  const registryRef = useRef(registry);
  registryRef.current = registry;

  useEffect(() => {
    registry?.publish(key, dirty);
  }, [registry, key, dirty]);

  useEffect(() => {
    return () => registryRef.current?.remove(key);
  }, [key]);
}
